import { Box, Divider, TabPanel, TabProps, TabsLayout } from '@televet/kibble-ui';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { ModalBody, ModalCloseButton, ModalHeader, ModalProps } from '@televet/kibble-ui/build/components/Modal';
import { useTracking } from 'shared/providers/TrackingProvider';
import { formatDuration, intervalToDuration } from 'date-fns';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { openAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import { default as React, useEffect, useMemo } from 'react';
import { IAvatarGroupSelectOption } from 'shared/components/Avatars/AvatarGroupSelect';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useNavigateToFormSubmission from 'shared/hooks/useNavigateToFormSubmission';
import {
  ChannelViewChannelFragment,
  ChannelViewClinicPetParentFragment,
  ChatEntityAppointmentFragment,
  ChatEntityFormSubmissionFragment,
  ClinicPetParent,
  useGetChatEntitiesQuery,
} from 'shared/types/graphql';
import { useAppDispatch } from 'state/hooks';
import { ChatEntityType } from '../enums/ChatEntityType';
import { AppointmentData } from '../types/AppointmentData';
import { AttachmentFormData } from '../types/AttachmentFormData';
import { AppointmentList } from './AppointmentList';
import { FormList } from './FormList';
import ParticipantsTab from './ParticipantsTab';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import withLazyModal from 'shared/components/LazyModal/withLazyModal';

interface ConversationDetailsModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  clinicPetParentIds: string[];
  petParentOptions: IAvatarGroupSelectOption[];
  handleLoadClientDropdownOptions: () => void;
  channelPetParents: ChannelViewClinicPetParentFragment[];
  isPetParentInAnotherConversation: (petParent: ClinicPetParent) => boolean;
  channel?: ChannelViewChannelFragment;
}

const formatAppointmentDuration = (durationInMinutes: number): string => {
  if (durationInMinutes < 60) {
    // If the duration is less than 60 minutes, display in minutes
    return `${durationInMinutes} minutes`;
  } else {
    // If the duration is 60 minutes or more, convert it to hours and minutes
    const duration = intervalToDuration({
      start: 0,
      end: durationInMinutes * 60 * 1000, // Convert minutes to milliseconds
    });

    return formatDuration({
      hours: duration.hours,
      minutes: duration.minutes,
    });
  }
};

const ConversationDetailsModal = ({
  clinicPetParentIds,
  petParentOptions,
  handleLoadClientDropdownOptions,
  channelPetParents,
  isPetParentInAnotherConversation,
  channel,
  isOpen,
  onClose,
}: ConversationDetailsModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const {
    data: chatEntitiesData,
    previousData: previousChatEntitiesData,
    refetch,
  } = useGetChatEntitiesQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    variables: {
      appointmentWhereInput: { clinicPetParents: { some: { id: { in: clinicPetParentIds } } } },
      formSubmissionsWhereInput: { clinicPetParent: { id: { in: clinicPetParentIds } } },
    },
  });
  const { navigateToFormSubmission } = useNavigateToFormSubmission();
  const { trackEvent } = useTracking();

  // Make sure to refetch the data when the modal is opened
  useEffect(() => {
    if (isOpen) {
      trackEvent(ConversationEvents.ChannelDetailsModalOpen);
      refetch();
    }
  }, [trackEvent, isOpen, refetch]);

  const handleChatEntitySelect = (id: string, type: ChatEntityType): void => {
    if (type === ChatEntityType.Appointment) {
      const appointment = chatEntitiesData?.appointments?.find((a: ChatEntityAppointmentFragment) => {
        return a.id === id;
      });
      if (appointment) {
        dispatch(openAppointmentModal({ appointmentId: appointment.id }));
      }
    } else {
      const formSubmission = chatEntitiesData?.formSubmissions?.find(
        (f: ChatEntityFormSubmissionFragment) => f.id === id,
      );
      if (formSubmission?.clinicPetParentId) {
        navigateToFormSubmission({
          formSubmissionId: formSubmission.id,
          clinicPetParentId: formSubmission.clinicPetParentId,
        });
      }
    }

    onClose();
  };

  const memoizedData = useMemo(() => {
    if (!chatEntitiesData) return { upcomingAppointments: [], pastAppointments: [], formSubmissions: [] };

    const isAppointmentUpcoming = (appointment: ChatEntityAppointmentFragment): boolean => {
      const appointmentDate = parseISO(appointment.startAt);
      const currentDate = new Date();
      return appointmentDate > currentDate;
    };

    const upcomingAppointments: AppointmentData[] = [];
    const pastAppointments: AppointmentData[] = [];
    const formSubmissions: AttachmentFormData[] = [];

    chatEntitiesData.appointments?.forEach((appointment: ChatEntityAppointmentFragment) => {
      const appointmentCard: AppointmentData = {
        id: appointment.id,
        title: appointment.appointmentType?.name || 'Appointment',
        startsAtStr: `${format(parseISO(appointment.startAt), 'MM/dd/yy')} at
        ${format(parseISO(appointment.startAt), 'h:mm aa')}`,
        doctor: `${appointment.clinicEmployees?.[0]?.firstName} ${appointment.clinicEmployees?.[0]?.lastName}` || '',
        duration: formatAppointmentDuration(appointment.durationInMinutes),
      };

      if (isAppointmentUpcoming(appointment)) {
        upcomingAppointments.push(appointmentCard);
      } else {
        pastAppointments.push(appointmentCard);
      }
    });

    chatEntitiesData.formSubmissions?.forEach((formSubmission: ChatEntityFormSubmissionFragment) => {
      const formData: AttachmentFormData = {
        id: formSubmission.id,
        title: formSubmission.formTemplate?.title || 'Unknown',
        createdAt: `${format(parseISO(formSubmission.createdAt), 'MM/dd/yy')} at
        ${format(parseISO(formSubmission.createdAt), 'h:mm aa')}`,
        isCompleted: !!formSubmission.submittedAt,
      };

      formSubmissions.push(formData);
    });

    return { upcomingAppointments, pastAppointments, formSubmissions };
  }, [chatEntitiesData]);

  const { upcomingAppointments, pastAppointments, formSubmissions } = memoizedData;

  const tabList = useMemo(() => {
    const tabs: TabProps[] = [
      {
        label: 'Participants',
        color: 'text.default',
        onClick: (): void => {
          trackEvent(ConversationEvents.ChannelDetailsModalTabParticipantsClick);
        },
      },
      {
        label: 'Appointments',
        color: 'text.default',
        onClick: (): void => {
          trackEvent(ConversationEvents.ChannelDetailsModalTabApptClick);
        },
      },
      {
        label: 'Forms',
        color: 'text.default',
        onClick: (): void => {
          trackEvent(ConversationEvents.ChannelDetailsModalTabFormClick);
        },
      },
    ];
    return tabs;
  }, [trackEvent]);

  return (
    <>
      <ModalHeader pb={1} pl={5}>
        <Heading size="md">Conversation Details</Heading>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody pt={0} px={0}>
        <Box height={400}>
          {!chatEntitiesData && !previousChatEntitiesData ? (
            <Box p={30}>
              <LoadingIndicator />
            </Box>
          ) : (
            <TabsLayout
              tabList={tabList}
              tabListProps={{
                position: 'sticky',
                top: 0,
                bg: 'white',
                zIndex: 1,
              }}
              tabPanels={[
                <TabPanel key="participants-tab" pt={5}>
                  <ParticipantsTab
                    petParentOptions={petParentOptions}
                    handleLoadClientDropdownOptions={handleLoadClientDropdownOptions}
                    channelPetParents={channelPetParents}
                    isPetParentInAnotherConversation={isPetParentInAnotherConversation}
                    channel={channel}
                    onClose={onClose}
                    clinicPetParentIds={clinicPetParentIds}
                  />
                </TabPanel>,
                <TabPanel key="appointment-tab" pt={5} px={4}>
                  <AppointmentList
                    title="Upcoming"
                    appointments={upcomingAppointments}
                    handleChatEntitySelect={handleChatEntitySelect}
                  />
                  <Divider borderBottomWidth="1px" borderBottomColor="border.default" mt={3} mb={5} />
                  <AppointmentList
                    title="Past"
                    appointments={pastAppointments}
                    handleChatEntitySelect={handleChatEntitySelect}
                  />
                </TabPanel>,
                <TabPanel key="form-tab" pt={4} px={4}>
                  <FormList forms={formSubmissions} handleChatEntitySelect={handleChatEntitySelect} />
                </TabPanel>,
              ]}
            />
          )}
        </Box>
      </ModalBody>
    </>
  );
};

export default withLazyModal(ConversationDetailsModal, {
  size: 'md',
  isCentered: true,
  scrollBehavior: 'inside',
  blockScrollOnMount: false,
  allowEscape: true,
  trapFocus: false,
});
